/**
 * Single source of truth for Ticket Type visuals + codes.
 *
 * The backend stores `ticket_type` as the display label ("Inbound" / "Service Call").
 * Use `typeKeyFromLabel(label)` to convert to a canonical key for lookups.
 */
import { PhoneIncoming, ClipboardList } from "lucide-react";

export const TICKET_TYPES = {
  inbound: {
    key: "inbound",
    label: "Inbound",
    description: "Live customer on the line — call, chat, WhatsApp",
    icon: PhoneIncoming,
    code_prefix: "INB",
    // Color tokens — emerald = live / go / customer is here now
    accent: "emerald",
    border: "border-emerald-500",
    topBar: "bg-emerald-500",
    pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
    btn: "bg-emerald-600 hover:bg-emerald-700 text-white",
    softBg: "bg-emerald-50/40",
    softBorder: "border-emerald-100",
    leftBar: "border-l-emerald-500",
    iconColor: "text-emerald-600",
    pulse: true,                          // animated dot on icon
    accelKey: "i",
  },
  service_call: {
    key: "service_call",
    label: "Service Call",
    description: "Offline / referral / walk-in / internal lead",
    icon: ClipboardList,
    code_prefix: "SVC",
    accent: "violet",
    border: "border-violet-500",
    topBar: "bg-violet-500",
    pill: "bg-violet-50 text-violet-700 border-violet-200",
    btn: "bg-violet-600 hover:bg-violet-700 text-white",
    softBg: "bg-violet-50/40",
    softBorder: "border-violet-100",
    leftBar: "border-l-violet-500",
    iconColor: "text-violet-600",
    pulse: false,
    accelKey: "s",
  },
};

/** Normalize a display label or code-prefix into a canonical key. */
export function typeKeyFromLabel(label) {
  if (!label) return null;
  const norm = String(label).trim().toLowerCase().replace(/\s+/g, "_");
  if (norm === "inbound" || norm === "inb") return "inbound";
  if (norm === "service_call" || norm === "svc") return "service_call";
  return null;
}

/** Look up the visual config from a display label. Returns null when unknown. */
export function ticketTypeMeta(label) {
  const k = typeKeyFromLabel(label);
  return k ? TICKET_TYPES[k] : null;
}

/** Smart default per the user's workstation_kind tag. */
export function defaultTypeForUser(user) {
  const w = user?.workstation_kind;
  if (w === "phone_agent") return "inbound";
  if (w === "backoffice") return "service_call";
  return null;
}
